import { Clickable } from '@carvana/merch-components';
import classnames from 'classnames';
import { useCallback, useContext, useMemo, useState } from 'react';
import Slider, { Settings } from 'react-slick';
import Sections from '~/constants/giveaway/sections';
import GlobalContext from '~/context/giveaway/GlobalContext';
import { StaticSpinnerData } from '~/contracts/common';
import CaretLeft from '~/icons/CaretLeft';
import CaretRight from '~/icons/CaretRight';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';
import Background from './Background';
import {
  ArrowButton,
  CarouselContainer,
  CarouselImage,
  CarouselWrapper,
  IconWrapper,
  ImageWrapper,
  Section,
  Title
} from './ImageCarousel.styles';
import { GhostCtaButton } from '../CtaButton';

function Dot({ isActive }: { isActive: boolean }) {
  return (
    <Clickable
      className={`${
        isActive ? 'bg-blue-2' : 'bg-grey-2'
      } h-[8px] w-[8px] rounded-full transition-all duration-500`}
      data-analytics-id="Carousel Dot"
    />
  );
}

type OwnProps = {
  spinnerData: StaticSpinnerData;
};

function ImageCarousel({ spinnerData }: OwnProps) {
  const { openHotspot } = useContext(GlobalContext);
  const [imageSliderNav, setImageSliderNav] = useState<Slider>();
  const [infoSliderNav, setInfoSliderNav] = useState<Slider>();
  const [currentSlide, setCurrentSlide] = useState(0);
  const { features } = spinnerData;

  const onPrevious = useCallback(() => {
    imageSliderNav?.slickPrev();
  }, [imageSliderNav]);

  const onNext = useCallback(() => {
    imageSliderNav?.slickNext();
  }, [imageSliderNav]);

  const getTranslationClassNames = useCallback(
    (index: number): string => {
      const length = features.length;
      const distanceRight = (index - currentSlide + length) % length;
      const distanceLeft = (currentSlide - index + length) % length;

      return classnames({
        'l:-translate-x-56 xl:-translate-x-72': distanceRight === 2,
        'l:translate-x-56 xl:translate-x-72': distanceLeft === 2
      });
    },
    [currentSlide, features.length]
  );

  const imageSlidersettings = useMemo<Settings>(
    () => ({
      arrows: false,
      asNavFor: infoSliderNav,
      beforeChange: (_, newSlide) => setCurrentSlide(newSlide),
      centerMode: true,
      centerPadding: '10px',
      className: 'pb-24 m:py-36 l:py-48',
      customPaging: i => <Dot isActive={currentSlide === i} />,
      dots: true,
      dotsClass:
        'absolute bottom-0 flex w-full justify-center slick-dots gap-4',
      infinite: true,
      ref: (imageSlider: Slider) => setImageSliderNav(imageSlider),
      responsive: [
        {
          breakpoint: 1024,
          settings: {
            slidesToShow: 1
          }
        }
      ],
      slidesToShow: 3,
      slidesToScroll: 1,
      touchThreshold: 10
    }),
    [currentSlide, infoSliderNav]
  );

  const infoSliderSettings = useMemo<Settings>(
    () => ({
      arrows: false,
      asNavFor: imageSliderNav,
      centerMode: true,
      centerPadding: '10px',
      fade: true,
      infinite: true,
      ref: (infoSlider: Slider) => setInfoSliderNav(infoSlider),
      slidesToShow: 1,
      slidesToScroll: 1,
      touchThreshold: 10
    }),
    [imageSliderNav]
  );

  return (
    <Section>
      <Title>Rig Rundown</Title>
      <CarouselContainer>
        <Background />
        <CarouselWrapper>
          <Slider {...imageSlidersettings}>
            {features.map(({ id, imageUrl, title }, index) => (
              <ImageWrapper key={id}>
                <CarouselImage
                  alt={title}
                  className={getTranslationClassNames(index)}
                  src={`${imageUrl}&width=450`}
                />
                <div
                  className={`overlay absolute inset-0 rounded-xl bg-white opacity-50 transition-all duration-300 ${getTranslationClassNames(
                    index
                  )}`}
                />
              </ImageWrapper>
            ))}
          </Slider>
          <ArrowButton
            className="left-36 l:left-0"
            data-analytics-id="Carousel Previous"
            onClick={onPrevious}
          >
            <IconWrapper>
              <CaretLeft className="text-blue-2" />
            </IconWrapper>
          </ArrowButton>
          <ArrowButton
            className="right-36 l:right-0"
            data-analytics-id="Carousel Next"
            onClick={onNext}
          >
            <IconWrapper>
              <CaretRight className="text-blue-2" />
            </IconWrapper>
          </ArrowButton>
        </CarouselWrapper>
      </CarouselContainer>
      <Slider {...infoSliderSettings}>
        {features.map(({ description, title }, index) => (
          <div
            className="flex w-full flex-col items-center justify-center gap-16 py-32 m:py-32 l:gap-24 l:py-56"
            key={`image-description-${index}`}
          >
            <div className="tracking-tight text-blue-6 tl-header-m m:tl-header-xl l:tl-header-xxl">
              {title}
            </div>
            <div className="px-24 text-center text-grey-5 tl-body-m m:max-w-[520px] l:max-w-[680px] l:tl-body-l">
              {description}
            </div>
          </div>
        ))}
      </Slider>
      <GhostCtaButton
        className="h-40 w-[172px]"
        data-analytics-id={'explore-button'}
        type="button"
        onClick={() => {
          openHotspot(features[currentSlide].id);
        }}
      >
        explore in 360
      </GhostCtaButton>
    </Section>
  );
}

export default withErrorBoundary(
  ImageCarousel,
  undefined,
  undefined,
  Sections.Gallery
);
